import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { KeycloakContext } from '../App';
import { EnvelopeIcon, EyeIcon, EyeSlashIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';

interface Domain {
  id: number;
  name: string;
}

interface EmailOffer {
  code: string;
  name: string;
  size_gb: number;
  price_ht: number;
  price_ttc: number;
}

interface EmailAccount {
  id: string;
  address?: string;
  local_part: string;
  domain: string;
  offer_name?: string;
  size_gb: number;
  password?: string;
  price_ht?: number;
  price_ttc?: number;
}

const Emails: React.FC = () => {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const location = useLocation();
  const { toast } = useToast();
  const [emailsList, setEmailsList] = useState<EmailAccount[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [offers, setOffers] = useState<EmailOffer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showOrderModal, setShowOrderModal] = useState<boolean>(false);
  const [orderSubmitting, setOrderSubmitting] = useState<boolean>(false);
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [localPart, setLocalPart] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [selectedEmailForDetails, setSelectedEmailForDetails] = useState<EmailAccount | null>(null);
  const [showEmailDetailsModal, setShowEmailDetailsModal] = useState<boolean>(false);
  const [showCredentials, setShowCredentials] = useState<boolean>(false);
  const [initialEmailOpened, setInitialEmailOpened] = useState<boolean>(false);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  useEffect(() => {
    if (initialEmailOpened || !keycloak?.token) return;
    const params = new URLSearchParams(location.search);
    const emailId = params.get('emailId');
    if (emailId) {
      openEmailDetails(emailId);
      setInitialEmailOpened(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, keycloak, initialEmailOpened]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [emailsRes, domainsRes, offersRes] = await Promise.all([
        fetch(`${API_URL}/emails`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
        fetch(`${API_URL}/domains`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
        fetch(`${API_URL}/emails/offers`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
      ]);
      if (!emailsRes.ok) throw new Error('Failed to fetch emails list');
      if (!domainsRes.ok) throw new Error('Failed to fetch domains');
      if (!offersRes.ok) throw new Error('Failed to fetch offers');
      const emailsData = await emailsRes.json();
      const domainsData = await domainsRes.json();
      const offersData = await offersRes.json();
      setEmailsList(emailsData || []);
      const normalizedDomains = (domainsData || []).map((d: any, index: number) =>
        typeof d === 'string' ? { id: index + 1, name: d } : d
      );
      setDomains(normalizedDomains);
      setOffers(offersData || []);
      setError(null);
    } catch (err: any) {
      console.error('Error loading emails data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openOrderModal = () => {
    setShowOrderModal(true);
    setSelectedDomain(null);
    setLocalPart('');
    setPassword('');
  };

  const closeOrderModal = () => {
    if (orderSubmitting) return;
    setShowOrderModal(false);
  };

  const handleSubmitOrder = async () => {
    if (!selectedDomain || !localPart || !password) { toast('Please fill in all fields', 'warning'); return; }
    const localPartRegex = /^[a-z0-9-]+$/;
    if (!localPartRegex.test(localPart)) { toast('Email local part can only contain lowercase letters, numbers, and hyphens', 'warning'); return; }
    setOrderSubmitting(true);
    try {
      const offer = offers[0];
      const response = await fetch(`${API_URL}/orders/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${keycloak?.token}` },
        body: JSON.stringify({ offerCode: offer.code, localPart, domain: selectedDomain.name, domainId: selectedDomain.id, password, priceHT: offer.price_ht, priceTTC: offer.price_ttc }),
      });
      if (!response.ok) { const errorText = await response.text(); throw new Error(`Failed to place order: ${errorText}`); }
      toast('Email order placed successfully! It will appear in your emails once validated by an admin.', 'success');
      setShowOrderModal(false);
      fetchData();
    } catch (err: any) {
      toast(`Failed to place order: ${err.message}`, 'error');
    } finally {
      setOrderSubmitting(false);
    }
  };

  const openWebmail = async (emailId: string, emailAddress: string, password?: string) => {
    let emailPassword = password;
    if (!emailPassword) {
      try {
        const response = await fetch(`${API_URL}/emails/${emailId}`, { headers: { Authorization: `Bearer ${keycloak?.token}` } });
        if (!response.ok) throw new Error('Failed to fetch email credentials');
        const emailData = await response.json();
        emailPassword = emailData.password;
      } catch (err: any) {
        toast(`Failed to fetch email credentials: ${err.message}`, 'error');
        return;
      }
    }
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = 'https://webmail.edj-labs.com/';
    form.target = '_blank';
    form.style.display = 'none';
    [{ name: '_user', value: emailAddress }, { name: '_pass', value: emailPassword || '' }, { name: '_host', value: 'ssl://ssl0.ovh.net:993' }]
      .forEach(({ name, value }) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const openEmailDetails = async (emailId: string) => {
    try {
      const response = await fetch(`${API_URL}/emails/${emailId}`, { headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!response.ok) throw new Error('Failed to fetch email details');
      const emailData = await response.json();
      setSelectedEmailForDetails(emailData);
      setShowEmailDetailsModal(true);
      setShowCredentials(false);
    } catch (err: any) {
      toast(`Failed to fetch email details: ${err.message}`, 'error');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={<EnvelopeIcon />}
          title="Emails"
          description="Manage your email accounts."
          actions={
            <button type="button" className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm" disabled>
              Order new Email
            </button>
          }
        />
        <div className="bg-card shadow rounded-lg overflow-hidden p-6 space-y-3 border border-border">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      </div>
    );
  }
  if (error) return <div className="rounded-md bg-destructive/10 p-4"><p className="text-sm text-destructive">Error: {error}</p></div>;

  const offer = offers[0];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<EnvelopeIcon />}
        title="Emails"
        description="Manage your email accounts."
        actions={
          <button type="button" onClick={openOrderModal} className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90">
            Order new Email
          </button>
        }
      />

      <div className="bg-card shadow rounded-lg overflow-hidden">
        <div className="w-full overflow-x-auto touch-pan-x">
        <table className="w-full min-w-[720px] divide-y divide-border">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Account</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Domain</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Offer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Size</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {emailsList.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-4 text-center text-sm text-muted-foreground">You do not have any email accounts yet.</td></tr>
            ) : (
              emailsList.map((email) => (
                <tr key={email.id} className="hover:bg-muted/40">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">{email.address || `${email.local_part}@${email.domain}`}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{email.domain}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{email.offer_name || 'Email Pro'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{email.size_gb} GB</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <div className="inline-flex items-center gap-2">
                      <button type="button" onClick={() => openWebmail(email.id, email.address || `${email.local_part}@${email.domain}`)} className="inline-flex items-center gap-1 rounded-md border border-primary px-3 py-1 text-xs font-medium text-primary hover:bg-primary/10">
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                        Webmail
                      </button>
                      <Button variant="outline" size="sm" onClick={() => openEmailDetails(email.id)}>Details</Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Order Modal */}
      {showOrderModal && offer && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm transition-opacity" aria-hidden="true" onClick={closeOrderModal} />
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-card rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div className="mb-4">
                <h3 className="text-lg font-medium leading-6 text-foreground">Order Email Pro 5GB</h3>
                <p className="mt-1 text-sm text-muted-foreground">Configure your professional email account</p>
              </div>
              <div className="space-y-4">
                <div className="bg-muted/30 rounded-lg p-4">
                  <h4 className="font-medium text-foreground">{offer.name}</h4>
                  <p className="text-sm text-muted-foreground mt-1">Storage: {offer.size_gb} GB</p>
                  <p className="text-sm text-foreground mt-2 font-semibold">€{offer.price_ht.toFixed(2)} excl. VAT / month (€{offer.price_ttc.toFixed(2)} incl. VAT)</p>
                </div>
                <div>
                  <label htmlFor="domain" className="block text-sm font-medium text-foreground">Select Domain</label>
                  <select id="domain" value={selectedDomain?.id || ''} onChange={(e) => { const domain = domains.find((d) => d.id === parseInt(e.target.value)); setSelectedDomain(domain || null); }}
                    className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm">
                    <option value="">Choose a domain</option>
                    {domains.map((domain) => <option key={domain.id} value={domain.id}>{domain.name}</option>)}
                  </select>
                  {domains.length === 0 && <p className="mt-1 text-sm text-red-600">You need to have at least one domain to order an email account.</p>}
                </div>
                <div>
                  <label htmlFor="localPart" className="block text-sm font-medium text-foreground">Email Account Name</label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input type="text" id="localPart" value={localPart} onChange={(e) => setLocalPart(e.target.value.toLowerCase())} placeholder="contact"
                      className="flex-1 min-w-0 block w-full px-3 py-2 rounded-l-md border-border focus:border-primary focus-visible:ring-ring sm:text-sm" />
                    <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-border bg-muted/30 text-muted-foreground sm:text-sm">@{selectedDomain?.name || 'domain.com'}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">Only lowercase letters, numbers, and hyphens allowed</p>
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-foreground">Password</label>
                  <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm" />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button type="button" onClick={closeOrderModal} disabled={orderSubmitting} className="inline-flex justify-center rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-muted/40">Cancel</button>
                <button type="button" onClick={handleSubmitOrder} disabled={orderSubmitting || !selectedDomain || !localPart || !password}
                  className="inline-flex justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
                  {orderSubmitting ? 'Placing order...' : 'Place Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Details Modal */}
      {showEmailDetailsModal && selectedEmailForDetails && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm transition-opacity" aria-hidden="true" onClick={() => setShowEmailDetailsModal(false)} />
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-card rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div className="mb-4"><h3 className="text-lg font-medium leading-6 text-foreground">Email Details</h3></div>
              <div className="space-y-4">
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-sm font-medium text-muted-foreground">Account</p><p className="mt-1 text-sm text-foreground">{selectedEmailForDetails.address || `${selectedEmailForDetails.local_part}@${selectedEmailForDetails.domain}`}</p></div>
                    <div><p className="text-sm font-medium text-muted-foreground">Domain</p><p className="mt-1 text-sm text-foreground">{selectedEmailForDetails.domain}</p></div>
                    <div><p className="text-sm font-medium text-muted-foreground">Offer</p><p className="mt-1 text-sm text-foreground">{selectedEmailForDetails.offer_name}</p></div>
                    <div><p className="text-sm font-medium text-muted-foreground">Size</p><p className="mt-1 text-sm text-foreground">{selectedEmailForDetails.size_gb} GB</p></div>
                    <div><p className="text-sm font-medium text-muted-foreground">Price (monthly)</p><p className="mt-1 text-sm text-foreground">{selectedEmailForDetails.price_ht != null ? `${selectedEmailForDetails.price_ht.toFixed(2).replace('.', ',')} € excl. VAT / ${selectedEmailForDetails.price_ttc?.toFixed(2).replace('.', ',')} € inc. VAT` : 'N/A'}</p></div>
                  </div>
                </div>
                <div className="bg-muted/30 rounded-lg p-4">
                  <h4 className="font-medium text-foreground mb-3">Credentials</h4>
                  <div className="space-y-3">
                    <div><p className="text-sm font-medium text-muted-foreground">Account</p><p className="mt-1 text-sm text-foreground">{selectedEmailForDetails.address || `${selectedEmailForDetails.local_part}@${selectedEmailForDetails.domain}`}</p></div>
                    <div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-muted-foreground">Password</p>
                        <button onClick={() => setShowCredentials(!showCredentials)} className="text-sm text-primary hover:text-primary/80">
                          {showCredentials ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className="mt-1 text-sm text-foreground font-mono">{showCredentials ? selectedEmailForDetails.password : '••••••••••'}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-muted/30 rounded-lg p-4">
                  <h4 className="font-medium text-foreground mb-3">Email Client Configuration</h4>
                  <div className="space-y-4 text-sm">
                    <div>
                      <p className="font-medium text-foreground">IMAP Configuration:</p>
                      <ul className="mt-1 space-y-1 text-muted-foreground">
                        <li>• Username: {selectedEmailForDetails.address || `${selectedEmailForDetails.local_part}@${selectedEmailForDetails.domain}`}</li>
                        <li>• Server (EUROPE - incoming): imap.mail.ovh.net or ssl0.ovh.net</li>
                        <li>• Server (AMERICA/ASIA-PACIFIC - incoming): imap.mail.ovh.ca</li>
                        <li>• Port: 993 | Security type: SSL/TLS</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">POP Configuration:</p>
                      <ul className="mt-1 space-y-1 text-muted-foreground">
                        <li>• Username: {selectedEmailForDetails.address || `${selectedEmailForDetails.local_part}@${selectedEmailForDetails.domain}`}</li>
                        <li>• Server (EUROPE - incoming): pop.mail.ovh.net or ssl0.ovh.net</li>
                        <li>• Server (AMERICA/ASIA-PACIFIC - incoming): pop.mail.ovh.ca</li>
                        <li>• Port: 995 | Security type: SSL/TLS</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">SMTP Configuration:</p>
                      <ul className="mt-1 space-y-1 text-muted-foreground">
                        <li>• Username: {selectedEmailForDetails.address || `${selectedEmailForDetails.local_part}@${selectedEmailForDetails.domain}`}</li>
                        <li>• Password: use the email account password above</li>
                        <li>• Server (EUROPE - outgoing): smtp.mail.ovh.net or ssl0.ovh.net</li>
                        <li>• Server (AMERICA/ASIA-PACIFIC - outgoing): smtp.mail.ovh.ca</li>
                        <li>• Port: 465 | Security type: SSL/TLS</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-between items-center">
                <button type="button" onClick={() => openWebmail(selectedEmailForDetails.id, selectedEmailForDetails.address || `${selectedEmailForDetails.local_part}@${selectedEmailForDetails.domain}`, selectedEmailForDetails.password)} className="inline-flex items-center gap-2 justify-center rounded-md border border-primary px-4 py-2 text-sm font-medium text-primary shadow-sm hover:bg-primary/10">
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  Open Webmail
                </button>
                <button type="button" onClick={() => setShowEmailDetailsModal(false)} className="inline-flex justify-center rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-muted/40">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Emails;
